import { API_BASE_URL, apiFetch } from "../api/client";
import { normalizeSeverity } from "../components/ui/SeverityBadge";
import type {
  AssuranceAssessment,
  AssuranceDecision,
  AssuranceEnvelope,
  AssuranceFinding,
  AssuranceLink,
  AssuranceMode,
  AssuranceRepository,
} from "./types";

// ---------------------------------------------------------------------------
// Tolerant value access
//
// Live assessments are assembled from whatever evidence the API measured, so
// every reader below treats absence as "not measured" rather than an error.
// ---------------------------------------------------------------------------

export function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function asRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {};
}

export function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

export function readString(
  record: Record<string, unknown>,
  aliases: string[]
): string | undefined {
  for (const alias of aliases) {
    const value = record[alias];
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number" || typeof value === "boolean") return String(value);
  }
  return undefined;
}

export function readNumber(
  record: Record<string, unknown>,
  aliases: string[]
): number | undefined {
  for (const alias of aliases) {
    const value = record[alias];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }
  return undefined;
}

export function readBoolean(
  record: Record<string, unknown>,
  aliases: string[]
): boolean | undefined {
  for (const alias of aliases) {
    const value = record[alias];
    if (typeof value === "boolean") return value;
  }
  return undefined;
}

function normalizeKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

export function findValue(
  root: unknown,
  aliases: string[],
  maxDepth = 5
): unknown {
  const wanted = new Set(aliases.map(normalizeKey));
  const seen = new Set<object>();

  function visit(value: unknown, depth: number): unknown {
    if (depth > maxDepth || (!isRecord(value) && !Array.isArray(value))) return undefined;
    if (typeof value === "object" && value !== null) {
      if (seen.has(value)) return undefined;
      seen.add(value);
    }

    if (isRecord(value)) {
      for (const [key, child] of Object.entries(value)) {
        if (wanted.has(normalizeKey(key))) return child;
      }
      for (const child of Object.values(value)) {
        const found = visit(child, depth + 1);
        if (found !== undefined) return found;
      }
    } else if (Array.isArray(value)) {
      for (const child of value.slice(0, 30)) {
        const found = visit(child, depth + 1);
        if (found !== undefined) return found;
      }
    }
    return undefined;
  }

  return visit(root, 0);
}

export function findSection(
  root: Record<string, unknown>,
  aliases: string[]
): unknown {
  return findValue(root, aliases, 4);
}

// ---------------------------------------------------------------------------
// Repository extraction
// ---------------------------------------------------------------------------

function repositoryFrom(value: unknown): AssuranceRepository | undefined {
  const record = asRecord(value);
  const nested =
    (isRecord(record.repository) && record.repository) ||
    (isRecord(record.repo) && record.repo) ||
    (isRecord(record.application) && record.application);
  const id = readString(record, ["id", "repoId", "repo_id", "repositoryId"]);
  const url = readString(record, ["url", "htmlUrl", "html_url", "repositoryUrl"]);
  const name =
    readString(record, ["name", "fullName", "full_name", "repository", "repo"]) ||
    (url ? url.replace(/\/+$/, "").split("/").slice(-2).join("/") : undefined);

  if (!id && !name && nested) return repositoryFrom(nested);
  if (!id && !name) return undefined;
  // Live portfolio rows nest their tallies under `counts`.
  const counts = asRecord(record.counts);
  return {
    id: id || name || "repository",
    name: name || id || "Repository",
    url,
    defaultBranch: readString(record, ["defaultBranch", "default_branch", "branch"]),
    description: readString(record, ["description", "summary"]),
    organization: readString(record, ["organization", "org", "owner"]),
    provider: readString(record, ["provider"]),
    commit: readString(record, ["commit", "commitSha", "latestCommit"]),
    commitShort: readString(record, ["commitShort", "commit_short"]),
    commitDate: readString(record, ["commitDate", "commit_date"]),
    release: readString(record, ["release", "version"]),
    visibility: readString(record, ["visibility"]),
    lastScanAt: readString(record, ["lastScanAt", "last_scan_at"]),
    lastScanStatus: readString(record, ["lastScanStatus", "last_scan_status"]),
    openFindings:
      readNumber(record, ["openFindings", "open_findings"]) ??
      readNumber(counts, ["open", "openFindings"]),
    openCritical:
      readNumber(record, ["openCritical", "open_critical"]) ??
      readNumber(counts, ["critical", "openCritical"]),
  };
}

function collectRepositoryCandidates(root: unknown): unknown[] {
  const direct = findValue(root, [
    "repositories",
    "repos",
    "applications",
    "portfolio",
  ]);
  if (Array.isArray(direct)) return direct;
  const record = asRecord(direct);
  for (const key of ["repositories", "repos", "applications", "items"]) {
    if (Array.isArray(record[key])) return record[key] as unknown[];
  }
  return [];
}

export function normalizePortfolio(envelope: AssuranceEnvelope): AssuranceRepository[] {
  const candidates = collectRepositoryCandidates(envelope.data);
  const repositories = candidates
    .map(repositoryFrom)
    .filter((repo): repo is AssuranceRepository => Boolean(repo));

  const unique = new Map(repositories.map((repo) => [repo.id, repo]));
  return Array.from(unique.values());
}

// ---------------------------------------------------------------------------
// Finding extraction
// ---------------------------------------------------------------------------

function traceStepsFrom(value: unknown) {
  return asArray(value)
    .map((step) => {
      if (typeof step === "string") return { detail: step };
      const record = asRecord(step);
      if (!Object.keys(record).length) return undefined;
      return {
        step: readString(record, ["step", "stage", "label", "title"]),
        detail: readString(record, ["detail", "description", "note", "code"]),
        file: readString(record, ["file", "filePath", "path"]),
        line: readNumber(record, ["line", "startLine"]),
        code: readString(record, ["code", "snippet"]),
      };
    })
    .filter((step): step is NonNullable<typeof step> => Boolean(step));
}

function referencesFrom(value: unknown) {
  return asArray(value)
    .map((reference) => {
      if (typeof reference === "string") {
        return reference.startsWith("http")
          ? { title: reference, url: reference }
          : { title: reference };
      }
      const record = asRecord(reference);
      const title = readString(record, ["title", "label", "name"]);
      const url = readString(record, ["url", "href"]);
      if (!title && !url) return undefined;
      return { title, url };
    })
    .filter((reference): reference is NonNullable<typeof reference> =>
      Boolean(reference)
    );
}

function findingFrom(value: unknown, index: number): AssuranceFinding | undefined {
  const raw = asRecord(value);
  if (!Object.keys(raw).length) return undefined;
  const location = asRecord(raw.location);

  const title = readString(raw, [
    "title",
    "name",
    "finding",
    "ruleName",
    "rule_name",
    "summary",
  ]);
  const severity = readString(raw, ["severity", "level", "risk", "priority"]);
  const file =
    readString(raw, ["file", "filePath", "file_path", "path"]) ||
    readString(location, ["file", "filePath", "file_path", "path"]);

  if (!title && !severity && !file) return undefined;

  const evidenceValue = raw.evidence ?? raw.provenance;
  return {
    id:
      readString(raw, ["id", "findingId", "finding_id", "fingerprint", "key"]) ||
      `finding-${index + 1}`,
    title: title || "Untitled finding",
    severity: normalizeSeverity(severity),
    domain:
      readString(raw, ["domain", "category", "kind", "type", "scanner"]) ||
      "Unclassified",
    status: readString(raw, ["status", "state", "disposition"]) || "Open",
    repository: readString(raw, ["repository", "repo", "repoName", "repo_name"]),
    file,
    line:
      readNumber(raw, ["line", "startLine", "start_line"]) ??
      readNumber(location, ["line", "startLine", "start_line"]),
    tool: readString(raw, ["tool", "scanner", "detectedBy", "detected_by", "engine"]),
    capability: readString(raw, ["capability"]),
    description: readString(raw, [
      "description",
      "details",
      "message",
      "rationale",
    ]),
    recommendation: readString(raw, [
      "recommendation",
      "remediation",
      "fix",
      "nextStep",
      "next_step",
    ]),
    evidence: evidenceValue,
    score: readNumber(raw, ["score", "riskScore", "cvss"]),
    scoreModel: readString(raw, ["scoreModel", "score_model"]),
    confidence: readString(raw, ["confidence", "confidenceLevel"]),
    corroboratedBy: asArray(raw.corroboratedBy)
      .map((v) => (typeof v === "string" ? v : undefined))
      .filter((v): v is string => Boolean(v)),
    additionalLocations: asArray(raw.additionalLocations).reduce<
      { file: string; line?: number }[]
    >((acc, v) => {
      const rec = asRecord(v);
      const file = readString(rec, ["file"]);
      if (file) acc.push({ file, line: readNumber(rec, ["line"]) });
      return acc;
    }, []),
    owasp: asArray(raw.owasp)
      .map((v) => (typeof v === "string" ? v : undefined))
      .filter((v): v is string => Boolean(v)),
    evidenceClass: readString(raw, ["evidenceClass", "evidence_class"]),
    priorityEvidence: readBoolean(raw, ["priorityEvidence", "priority_evidence"]),
    cwe: readString(raw, ["cweName", "cwe"]),
    cve: readString(raw, ["cve", "cveId", "cve_id"]),
    package: readString(raw, ["package", "packageName", "package_name"]),
    version: readString(raw, ["version", "packageVersion", "package_version"]),
    fixVersion: readString(raw, ["fixVersion", "fixedVersion", "fix_version", "fixedIn"]),
    upgradeJump: readString(raw, ["upgradeJump", "upgrade_jump"]),
    upgradeEffort: readString(raw, ["upgradeEffort", "upgrade_effort"]),
    usageSites: readNumber(raw, ["usageSites", "usage_sites"]),
    exposure: readString(raw, ["exposure"]),
    exposureBasis: readString(raw, ["exposureBasis", "exposure_basis"]),
    malicious: readBoolean(raw, ["malicious"]),
    dependencyRelationship: readString(raw, ["dependencyRelationship", "dependency_relationship"]),
    dependencyPath: asArray(raw.dependencyPath ?? raw.dependency_path)
      .map((v) => (typeof v === "string" ? v : undefined))
      .filter((v): v is string => Boolean(v)),
    introducedBy: readString(raw, ["introducedBy", "introduced_by"]),
    source: readString(raw, ["source"]),
    sink: readString(raw, ["sink"]),
    trace: traceStepsFrom(raw.trace),
    evidenceChain: traceStepsFrom(raw.evidenceChain),
    // Prefer the real captured code (CodeQL/Opengrep snippet) so live findings
    // show the exact vulnerable code, not just the evidence JSON.
    evidenceSnippet:
      readString(raw, ["codeSnippet", "code_snippet"]) ||
      readString(raw, ["codeContext", "code_context"]) ||
      (typeof evidenceValue === "string"
        ? evidenceValue
        : readString(asRecord(evidenceValue), ["snippet", "code", "content"])),
    exploitability: readString(raw, ["exploitability"]),
    exploitScenario: readString(raw, ["exploitScenario", "exploit_scenario"]),
    businessImpact: readString(raw, ["businessImpact", "business_impact", "impact"]),
    validationSteps: asArray(raw.validationSteps)
      .map((step) => (typeof step === "string" ? step : undefined))
      .filter((step): step is string => Boolean(step)),
    owner: readString(raw, ["owner", "team", "assignee"]),
    sla: readString(raw, ["sla", "dueDate", "due_date"]),
    firstSeen: readString(raw, ["firstSeen", "first_seen", "firstSeenAt"]),
    lastSeen: readString(raw, ["lastSeen", "last_seen", "lastSeenAt"]),
    introducedCommit: readString(raw, ["introducedCommit", "introduced_commit"]),
    references: referencesFrom([
      ...asArray(raw.references),
      ...(readString(raw, ["helpUri", "help_uri"]) ? [readString(raw, ["helpUri", "help_uri"])] : []),
    ]),
    tags: asArray(raw.tags)
      .map((tag) => (typeof tag === "string" ? tag : undefined))
      .filter((tag): tag is string => Boolean(tag)),
    auditTrail: asArray(raw.auditTrail)
      .map((event) => {
        const record = asRecord(event);
        if (!Object.keys(record).length) return undefined;
        return {
          at: readString(record, ["at", "timestamp", "date"]),
          actor: readString(record, ["actor", "by", "user"]),
          action: readString(record, ["action", "event", "detail"]),
        };
      })
      .filter((event): event is NonNullable<typeof event> => Boolean(event)),
    raw,
  };
}

function collectFindings(root: Record<string, unknown>): AssuranceFinding[] {
  const value = findValue(root, [
    "findings",
    "findingInventory",
    "finding_inventory",
    "issues",
    "vulnerabilities",
    "risks",
  ]);
  const candidates = Array.isArray(value)
    ? value
    : asArray(
        asRecord(value).items ??
          asRecord(value).findings ??
          asRecord(value).inventory ??
          asRecord(value).records ??
          asRecord(value).data
      );

  return candidates
    .map(findingFrom)
    .filter((finding): finding is AssuranceFinding => Boolean(finding));
}

// ---------------------------------------------------------------------------
// Link discovery
// ---------------------------------------------------------------------------

function isSafeHref(value: string): boolean {
  return (
    value.startsWith("/") ||
    value.startsWith("https://") ||
    value.startsWith("http://")
  );
}

function collectLinks(root: unknown): AssuranceLink[] {
  const links: AssuranceLink[] = [];
  const seen = new Set<object>();

  function visit(value: unknown, depth: number, parentKey = "") {
    if (depth > 5 || (!isRecord(value) && !Array.isArray(value))) return;
    if (typeof value === "object" && value !== null) {
      if (seen.has(value)) return;
      seen.add(value);
    }

    if (Array.isArray(value)) {
      value.slice(0, 40).forEach((item) => visit(item, depth + 1, parentKey));
      return;
    }

    const record = value as Record<string, unknown>;
    const href = readString(record, ["href", "url", "downloadUrl", "download_url"]);
    if (href && isSafeHref(href)) {
      links.push({
        href,
        label:
          readString(record, ["label", "name", "title", "format"]) ||
          parentKey ||
          "Open endpoint",
        kind: readString(record, ["kind", "type", "format"]),
      });
    }

    for (const [key, child] of Object.entries(record)) {
      if (
        typeof child === "string" &&
        /(url|href|endpoint|download)/i.test(key) &&
        isSafeHref(child)
      ) {
        links.push({ href: child, label: key.replace(/[_-]/g, " ") });
      } else {
        visit(child, depth + 1, key.replace(/[_-]/g, " "));
      }
    }
  }

  visit(root, 0);
  const unique = new Map(links.map((link) => [`${link.label}|${link.href}`, link]));
  return Array.from(unique.values());
}

// ---------------------------------------------------------------------------
// Decision derivation
// ---------------------------------------------------------------------------

const FAIL_STATUSES = new Set(["fail", "failed", "blocked", "gap"]);
const REVIEW_STATUSES = new Set(["review", "partial", "warn", "warning", "conditional"]);
const PASS_STATUSES = new Set(["pass", "passed", "ok", "satisfied", "compliant"]);

export function deriveDecision(policies: unknown[]): AssuranceDecision {
  const records = policies.filter(isRecord);
  let failing = 0;
  let review = 0;
  let passing = 0;
  for (const policy of records) {
    const status = (readString(policy, ["status", "state", "result"]) || "")
      .toLowerCase();
    if (FAIL_STATUSES.has(status)) failing += 1;
    else if (REVIEW_STATUSES.has(status)) review += 1;
    else if (PASS_STATUSES.has(status)) passing += 1;
  }
  const total = records.length;
  // "pass" requires at least one gate that actually passed: policies whose
  // status is unrecognized (e.g. live-mode "requires_validation") never
  // count as passing.
  return {
    state: failing
      ? "blocked"
      : review
        ? "review"
        : passing
          ? "pass"
          : "unknown",
    failing,
    review,
    passing,
    total,
  };
}

// ---------------------------------------------------------------------------
// Assessment normalization
// ---------------------------------------------------------------------------

function unwrapAssessment(data: unknown): Record<string, unknown> {
  const record = asRecord(data);
  const wrapped = record.assessment ?? record.application ?? record.repositoryAssessment;
  return isRecord(wrapped) ? wrapped : record;
}

export function normalizeAssessment(
  envelope: AssuranceEnvelope,
  fallbackMode: AssuranceMode
): AssuranceAssessment {
  const source = unwrapAssessment(envelope.data);
  const repository =
    repositoryFrom(source.repository) ||
    repositoryFrom(source.repo) ||
    repositoryFrom(source.application) ||
    repositoryFrom(source);
  const findings = collectFindings(source).map((finding) =>
    finding.repository || !repository
      ? finding
      : { ...finding, repository: repository.name }
  );

  return {
    mode: envelope.mode || fallbackMode,
    generatedAt: envelope.generatedAt,
    notice: envelope.notice,
    repository,
    source,
    findings,
    links: collectLinks(source),
  };
}

// ---------------------------------------------------------------------------
// API access
// ---------------------------------------------------------------------------

export async function fetchAssuranceDemo(): Promise<AssuranceEnvelope> {
  return apiFetch<AssuranceEnvelope>({ path: "/v1/assurance/demo" });
}

export async function fetchAssurancePortfolio(): Promise<AssuranceEnvelope> {
  return apiFetch<AssuranceEnvelope>({ path: "/v1/assurance/portfolio" });
}

export async function fetchRepositoryAssurance(
  repoId: string
): Promise<AssuranceEnvelope> {
  return apiFetch<AssuranceEnvelope>({
    path: `/v1/assurance/repos/${encodeURIComponent(repoId)}`,
  });
}

export function apiHref(path: string): string {
  const base = API_BASE_URL.replace(/\/+$/, "");
  return base ? `${base}${path}` : path;
}

// ---------------------------------------------------------------------------
// Report runs (first-class asynchronous report generation)
// ---------------------------------------------------------------------------

export interface ReportTypeInfo {
  id: string;
  title: string;
  audience: string;
  description: string;
  sections: string[];
}

export interface ReportFormatInfo {
  id: string;
  label: string;
  contentType: string;
}

export interface ReportTemplateCatalog {
  templateVersion: string;
  reportTypes: ReportTypeInfo[];
  formats: ReportFormatInfo[];
  progressStages: string[];
  defaults: Record<string, unknown>;
}

export interface ReportRunArtifact {
  name: string;
  format: string;
  contentType: string;
  filename: string;
  bytes: number;
  sha256: string;
  createdAt?: string;
}

export interface ReportRun {
  id: string;
  repoId: string;
  scanId: string;
  commit: string;
  branch?: string;
  reportType: string;
  reportTitle: string;
  formats: string[];
  options: Record<string, unknown>;
  status: string;
  progressStage?: string;
  progressPercent?: number;
  requestedBy?: string;
  requestedAt?: string;
  startedAt?: string;
  completedAt?: string;
  failedAt?: string;
  failureReason?: string;
  findingCount?: number;
  severityCounts: Record<string, unknown>;
  templateVersion?: string;
  retentionUntil?: string;
  artifacts?: ReportRunArtifact[];
}

function reportRunFrom(value: unknown): ReportRun | undefined {
  const record = asRecord(value);
  const id = readString(record, ["id"]);
  if (!id) return undefined;
  return {
    id,
    repoId: readString(record, ["repoId"]) || "",
    scanId: readString(record, ["scanId"]) || "",
    commit: readString(record, ["commit"]) || "not_measured",
    branch: readString(record, ["branch"]),
    reportType: readString(record, ["reportType"]) || "comprehensive",
    reportTitle: readString(record, ["reportTitle"]) || "Assurance report",
    formats: asArray(record.formats).map(String),
    options: asRecord(record.options),
    status: readString(record, ["status"]) || "queued",
    progressStage: readString(record, ["progressStage"]),
    progressPercent: readNumber(record, ["progressPercent"]),
    requestedBy: readString(record, ["requestedBy"]),
    requestedAt: readString(record, ["requestedAt"]),
    startedAt: readString(record, ["startedAt"]),
    completedAt: readString(record, ["completedAt"]),
    failedAt: readString(record, ["failedAt"]),
    failureReason: readString(record, ["failureReason"]),
    findingCount: readNumber(record, ["findingCount"]),
    severityCounts: asRecord(record.severityCounts),
    templateVersion: readString(record, ["templateVersion"]),
    retentionUntil: readString(record, ["retentionUntil"]),
    artifacts: asArray(record.artifacts).map((artifact) => {
      const item = asRecord(artifact);
      return {
        name: readString(item, ["name"]) || "artifact",
        format: readString(item, ["format"]) || "html",
        contentType: readString(item, ["contentType"]) || "text/html",
        filename: readString(item, ["filename"]) || "report",
        bytes: readNumber(item, ["bytes"]) ?? 0,
        sha256: readString(item, ["sha256"]) || "",
        createdAt: readString(item, ["createdAt"]),
      };
    }),
  };
}

export async function fetchReportTemplates(): Promise<ReportTemplateCatalog> {
  const response = await apiFetch<Record<string, unknown>>({
    path: "/v1/assurance/report-templates",
  });
  return {
    templateVersion: readString(response, ["templateVersion"]) || "",
    reportTypes: asArray(response.reportTypes).map((type) => {
      const record = asRecord(type);
      return {
        id: readString(record, ["id"]) || "",
        title: readString(record, ["title"]) || "",
        audience: readString(record, ["audience"]) || "",
        description: readString(record, ["description"]) || "",
        sections: asArray(record.sections).map(String),
      };
    }),
    formats: asArray(response.formats).map((format) => {
      const record = asRecord(format);
      return {
        id: readString(record, ["id"]) || "",
        label: readString(record, ["label"]) || "",
        contentType: readString(record, ["contentType"]) || "",
      };
    }),
    progressStages: asArray(response.progressStages).map(String),
    defaults: asRecord(response.defaults),
  };
}

export interface ReportRunListParams {
  status?: string;
  type?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export async function fetchReportRuns(
  repoId: string,
  params: ReportRunListParams = {}
): Promise<{ reports: ReportRun[]; total: number }> {
  const query = new URLSearchParams();
  if (params.status) query.set("status", params.status);
  if (params.type) query.set("type", params.type);
  if (params.q) query.set("q", params.q);
  if (params.limit) query.set("limit", String(params.limit));
  if (params.offset) query.set("offset", String(params.offset));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  const response = await apiFetch<{ reports?: unknown[]; total?: number }>({
    path: `/v1/assurance/repos/${encodeURIComponent(repoId)}/reports${suffix}`,
  });
  return {
    reports: asArray(response.reports)
      .map(reportRunFrom)
      .filter((run): run is ReportRun => Boolean(run)),
    total: Number(response.total || 0),
  };
}

export interface CreateReportRequest {
  reportType: string;
  formats: string[];
  options: Record<string, unknown>;
}

export async function createReportRun(
  repoId: string,
  request: CreateReportRequest
): Promise<{ existing: boolean; report: ReportRun | undefined }> {
  const response = await apiFetch<{ existing?: boolean; report?: unknown }>({
    path: `/v1/assurance/repos/${encodeURIComponent(repoId)}/reports`,
    method: "POST",
    body: request,
  });
  return { existing: Boolean(response.existing), report: reportRunFrom(response.report) };
}

export async function fetchReportRun(
  repoId: string,
  reportId: string
): Promise<ReportRun | undefined> {
  const response = await apiFetch<{ report?: unknown }>({
    path: `/v1/assurance/repos/${encodeURIComponent(repoId)}/reports/${encodeURIComponent(reportId)}`,
  });
  return reportRunFrom(response.report);
}

export async function fetchReportRunStatus(
  repoId: string,
  reportId: string
): Promise<Record<string, unknown>> {
  return apiFetch<Record<string, unknown>>({
    path: `/v1/assurance/repos/${encodeURIComponent(repoId)}/reports/${encodeURIComponent(reportId)}/status`,
  });
}

export async function regenerateReportRun(
  repoId: string,
  reportId: string
): Promise<ReportRun | undefined> {
  const response = await apiFetch<{ report?: unknown }>({
    path: `/v1/assurance/repos/${encodeURIComponent(repoId)}/reports/${encodeURIComponent(reportId)}/regenerate`,
    method: "POST",
    body: {},
  });
  return reportRunFrom(response.report);
}

/** Reason categories accepted by the suppression API. */
export const SUPPRESSION_REASONS = [
  { value: "false_positive", label: "False positive" },
  { value: "not_exploitable", label: "Not exploitable in this context" },
  { value: "compensating_control", label: "Compensating control in place" },
  { value: "accepted_risk", label: "Accepted risk" },
  { value: "fix_planned", label: "Fix already planned" },
  { value: "other", label: "Other" },
];

export async function suppressFinding(
  findingId: string,
  category: string,
  reason: string,
  expiresAt?: string
): Promise<{ success: boolean; expires_at?: string }> {
  return apiFetch<{ success: boolean; expires_at?: string }>({
    path: `/findings/${encodeURIComponent(findingId)}/suppress`,
    method: "POST",
    body: { category, reason, ...(expiresAt ? { expires_at: expiresAt } : {}) },
  });
}

export async function unsuppressFinding(findingId: string): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>({
    path: `/findings/${encodeURIComponent(findingId)}/suppress`,
    method: "DELETE",
  });
}

export async function shareReportRun(
  repoId: string,
  reportId: string,
  formats?: string[]
): Promise<{ url: string; expiresAt?: string; formats?: string[] }> {
  return apiFetch<{ url: string; expiresAt?: string; formats?: string[] }>({
    path: `/v1/assurance/repos/${encodeURIComponent(repoId)}/reports/${encodeURIComponent(reportId)}/share`,
    method: "POST",
    // Scope the link to presentation formats — a share must not hand over the
    // full JSON/CSV evidence bundle unless that is explicitly intended.
    body: formats && formats.length ? { formats } : {},
  });
}

export async function deleteReportRun(repoId: string, reportId: string): Promise<void> {
  await apiFetch<{ deleted?: boolean }>({
    path: `/v1/assurance/repos/${encodeURIComponent(repoId)}/reports/${encodeURIComponent(reportId)}`,
    method: "DELETE",
  });
}

export function reportPreviewHref(repoId: string, reportId: string): string {
  return apiHref(
    `/v1/assurance/repos/${encodeURIComponent(repoId)}/reports/${encodeURIComponent(reportId)}/preview`
  );
}

export function reportDownloadHref(repoId: string, reportId: string, format: string): string {
  return apiHref(
    `/v1/assurance/repos/${encodeURIComponent(repoId)}/reports/${encodeURIComponent(
      reportId
    )}/download?format=${encodeURIComponent(format)}`
  );
}
